import { useState } from 'react';
import { Link } from '@backstage/core-components';
import {
  RDAP_LOGO_URL_ANNOTATION,
  getEntityInitials,
  getEntityLogoUrl,
} from '@internal/backstage-plugin-catalog-rdap-frontend';

export { RDAP_LOGO_URL_ANNOTATION };

export type CatalogHomeRelation = {
  type: string;
  targetRef: string;
};

export type CatalogHomeEntity = {
  kind: string;
  metadata: {
    name: string;
    namespace?: string;
    title?: string;
    description?: string;
    annotations?: Record<string, string>;
  };
  spec?: {
    owner?: string;
    lifecycle?: string;
    type?: string;
  };
  relations?: CatalogHomeRelation[];
};

export type CatalogHomeItem = {
  title: string;
  subtitle?: string;
  to: string;
  namespace?: string;
  initials: string;
  logoUrl?: string;
  entityRef: string;
  ownerRef?: string;
  lifecycle?: string;
  type?: string;
};

export const getCatalogHomeInitials = getEntityInitials;

export const getCatalogHomeLogoUrl = (
  entity: CatalogHomeEntity,
): string | undefined => getEntityLogoUrl(entity);

export const getCatalogEntityRef = (
  entity: CatalogHomeEntity,
  kind: string,
): string => {
  const namespace = entity.metadata.namespace ?? 'default';
  const normalizedKind = kind.toLocaleLowerCase('en-US');

  return `${normalizedKind}:${namespace}/${entity.metadata.name}`;
};

export const toCatalogHomeItem = (
  entity: CatalogHomeEntity,
  kind: string,
  subtitle?: string,
): CatalogHomeItem => {
  const namespace = entity.metadata.namespace ?? 'default';
  const title = entity.metadata.title ?? entity.metadata.name;
  const normalizedKind = kind.toLocaleLowerCase('en-US');

  return {
    title,
    subtitle,
    to: `/catalog/${namespace}/${normalizedKind}/${entity.metadata.name}`,
    namespace,
    initials: getCatalogHomeInitials(title),
    logoUrl: getCatalogHomeLogoUrl(entity),
    entityRef: getCatalogEntityRef(entity, kind),
    ...(entity.spec?.owner ? { ownerRef: entity.spec.owner } : {}),
    ...(entity.spec?.lifecycle ? { lifecycle: entity.spec.lifecycle } : {}),
    ...(entity.spec?.type ? { type: entity.spec.type } : {}),
  };
};

type CatalogHomeCardProps = {
  item: CatalogHomeItem;
  ariaLabel: string;
};

export const CatalogHomeCard = ({
  item,
  ariaLabel,
}: CatalogHomeCardProps) => {
  const [hasLogoError, setHasLogoError] = useState(false);
  const shouldShowLogo = item.logoUrl && !hasLogoError;

  return (
    <Link
      to={item.to}
      className="rdap-home-card"
      aria-label={ariaLabel}
      data-entity-ref={item.entityRef}
      underline="none"
    >
      <span className="rdap-home-card-mark" aria-hidden="true">
        {shouldShowLogo ? (
          <img
            className="rdap-home-card-logo"
            src={item.logoUrl}
            alt=""
            onError={() => setHasLogoError(true)}
          />
        ) : (
          item.initials
        )}
      </span>

      <span className="rdap-home-card-copy">
        <span className="rdap-home-card-title">
          {item.title}
        </span>

        {item.subtitle ? (
          <span className="rdap-home-card-meta">
            {item.subtitle}
          </span>
        ) : null}
      </span>
    </Link>
  );
};